import { useState } from 'react';
import { type LucideIcon } from 'lucide-react';
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem
} from '@/components/ui/sidebar';
import { Link, matchPath, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { getMerchantScopedUrl } from '@/utils/url-helpers';

type Item = {
  name: string;
  url?: string;
  icon?: LucideIcon;
  items?: Omit<Required<Item>, 'items' | 'icon'>[];
};

export function NavMain({ items }: { items: Item[] }) {
  const params = useParams<{ slug: string }>();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const [expanded, setExpanded] = useState<string | null>(null);

  const getNavigationUrl = (url: string) => {
    return getMerchantScopedUrl(params.slug!, url, searchParams);
  };

  const toggleExpand = (name: string) => {
    setExpanded((prev) => (prev === name ? null : name));
  };

  // Helper function to check if any sub-item is active
  const isParentActive = (item: Item) => {
    return (
      item.items?.some((subItem) => matchPath(`/merchants/:slug${subItem.url}`, pathname)) ?? false
    );
  };

  return (
    <SidebarGroup>
      <SidebarMenu className="gap-2">
        {items.map((item) => (
          <SidebarMenuItem key={item.name} className="group/collapsible">
            <SidebarMenuButton
              tooltip={item.name}
              asChild
              isActive={item.url ? !!matchPath(`/merchants/:slug${item.url}`, pathname) : false}
              onClick={() => {
                if (item.items) {
                  toggleExpand(item.name);
                }
              }}
            >
              {item.url ? (
                <Link to={getNavigationUrl(item.url)}>
                  {item.icon && <item.icon />}
                  <span>{item.name}</span>
                </Link>
              ) : (
                <Link to={getNavigationUrl(item.items?.[0].url ?? '')}>
                  {item.icon && <item.icon />}
                  <span>{item.name}</span>
                </Link>
              )}
            </SidebarMenuButton>
            {item.items?.length && (expanded === item.name || isParentActive(item)) ? (
              <SidebarMenuSub className="ml-4 mr-0 pr-0 border-l-[1px]">
                {item.items.map((subItem) => (
                  <SidebarMenuSubItem key={subItem.name}>
                    <SidebarMenuSubButton
                      asChild
                      isActive={!!matchPath(`/merchants/:slug${subItem.url}`, pathname)}
                      className="p-2"
                    >
                      <Link to={getNavigationUrl(subItem.url)}>{subItem.name}</Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            ) : null}
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}
